(() => {
  class CompleteTheBundle {
    constructor(el) {
      this.el         = el;
      this.track      = el.querySelector('.ctb__track');
      this.layout     = el.dataset.layout;       // "carousel" | "grid"
      this.fetchMode  = el.dataset.fetchMode;    // "manual" | "auto"
      this.productId  = el.dataset.productId;
      this.max        = parseInt(el.dataset.max, 10) || 6;
      this.perRow     = parseInt(el.dataset.cardsPerRow, 10) || 3;
      this.index      = 0;

      if (this.fetchMode === 'auto') {
        this._fetchRecommendations();
      } else {
        this._init();
      }
    }

    /* ─── Recommendations API (auto mode) ─── */
    async _fetchRecommendations() {
      try {
        const res = await fetch(
          `/recommendations/products.json?product_id=${this.productId}&limit=${this.max}&intent=related`
        );
        if (!res.ok) throw new Error();
        const { products } = await res.json();
        if (!products?.length) { this.el.style.display = 'none'; return; }
        this._renderCards(products);
        this._init();
      } catch {
        this.el.style.display = 'none';
      }
    }

    _renderCards(products) {
      const btn = this.el.dataset.btnLabel ||
        this.el.querySelector('.ctb__card-btn')?.textContent.trim() || 'ADD +';

      this.track.innerHTML = products.map((p) => `
        <li class="ctb__card">
          <a href="${p.url}" class="ctb__card-img">
            <img src="${p.featured_image}" alt="${this._esc(p.title)}"
                 width="400" height="400" loading="lazy">
          </a>
          <p class="ctb__card-title">${this._esc(p.title)}</p>
          <p class="ctb__card-price">${this._money(p.price)}</p>
          <button class="ctb__card-btn" data-variant-id="${p.variants[0]?.id}">${btn}</button>
        </li>`).join('');
    }

    /* ─── Init after cards are in DOM ─── */
    _init() {
      this._bindAddToCart();
      if (this.layout === 'carousel') {
        this._bindArrows();
        this._updateArrows();
        window.addEventListener('resize', () => {
          this.index = 0;
          this._slide();
          this._updateArrows();
        });
      }
    }

    /* ─── Add to cart ─── */
    _bindAddToCart() {
      this.track.querySelectorAll('.ctb__card-btn').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const variantId = btn.dataset.variantId;
          if (!variantId) return;

          const orig = btn.textContent;
          btn.disabled = true;

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: variantId, quantity: 1 }),
            });

            if (res.ok) {
              btn.textContent = '✓ Added';
              document.dispatchEvent(new CustomEvent('cart:updated'));
              setTimeout(() => {
                btn.textContent = orig;
                btn.disabled = false;
              }, 2000);
            } else {
              const err = await res.json();
              alert(err.description || 'Could not add to cart.');
              btn.disabled = false;
            }
          } catch {
            btn.disabled = false;
          }
        });
      });
    }

    /* ─── Carousel arrows ─── */
    _bindArrows() {
      this.prevBtn = this.el.querySelector('.ctb__arrow--prev');
      this.nextBtn = this.el.querySelector('.ctb__arrow--next');

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.index = Math.max(0, this.index - 1);
          this._slide();
        });
        this.nextBtn.addEventListener('click', () => {
          this.index = Math.min(this._maxIndex(), this.index + 1);
          this._slide();
        });
      }

      this._bindSwipe();
    }

    /* ─── Touch swipe ─── */
    _bindSwipe() {
      const wrap = this.el.querySelector('.ctb__track-wrap');
      let startX = 0;
      let isDragging = false;

      wrap.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      }, { passive: true });

      wrap.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          if (diff > 0) {
            this.index = Math.min(this._maxIndex(), this.index + 1);
          } else {
            this.index = Math.max(0, this.index - 1);
          }
          this._slide();
        }
        isDragging = false;
      }, { passive: true });
    }

    _slide() {
      const cards = this._cards();
      if (!cards.length) return;
      const cardW = cards[0].offsetWidth + 8;
      this.track.style.transform = `translateX(-${this.index * cardW}px)`;
      this._updateArrows();
    }

    _updateArrows() {
      if (!this.prevBtn) return;
      this.prevBtn.disabled = this.index === 0;
      this.nextBtn.disabled = this.index >= this._maxIndex();
    }

    _maxIndex() {
      return Math.max(0, this._cards().length - this._visible());
    }

    _cards() {
      return [...this.track.querySelectorAll('.ctb__card')];
    }

    _visible() {
      const w = window.innerWidth;
      if (w <= 480) return 1;
      if (w <= 768) return 2;
      return this.perRow;
    }

    /* ─── Helpers ─── */
    _money(cents) {
      if (window.Shopify?.formatMoney) return Shopify.formatMoney(cents, '${{amount}}');
      return '$' + (cents / 100).toFixed(2);
    }

    _esc(s) {
      return String(s).replace(/[&<>"']/g, (c) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
      );
    }
  }

  /* ─── Boot ─── */
  const init = () => {
    document.querySelectorAll('.ctb:not([data-ctb-ready])').forEach((el) => {
      el.dataset.ctbReady = '1';
      new CompleteTheBundle(el);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor live reload
  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.ctb');
    if (el) { el.dataset.ctbReady = '1'; new CompleteTheBundle(el); }
  });
})();
